/**
 * Die Kennzahlen der Uebersicht „Uebersicht & Historie" - `renderOverview` (`index.html:14561-14858`),
 * so wie `components/OverviewPage/UebersichtSeite.tsx` sie bis zum KI-Zugang (Stufe 1) in einem
 * `useMemo` fuehrte. Sie sind UNVERAENDERT in diese Funktion gezogen, weil der Server
 * (`backend/src/ki/auswertung/frontend/`) dieselben Kennzahlen rechnen muss wie die Oberflaeche und
 * dafuer eine zeichengleiche Kopie dieses Moduls braucht; die Seite ruft die Funktion im `useMemo` und
 * rendert das Ergebnis. Das Ergebnisobjekt traegt dieselben Schluessel wie das damalige `useMemo`
 * (`today` ... `progressPct`).
 *
 * Alle Eigenheiten, die die Seite dokumentiert, gelten weiter (Kopfkommentar von `UebersichtSeite.tsx`,
 * Abschnitt „Woertlich uebernommene Eigenheiten"): `active` und `workflowOrders` sind nicht dasselbe
 * (die Zahlungsansicht nimmt auch archivierte Bestellungen mit offener Zahlungsaufgabe mit, die Kacheln
 * ausser „Dringend" und „Offene Tasks" rechnen aber weiter ueber `active`), `openPayments` filtert das
 * Rohfeld `!payment.paid`, `arrivingSoon` ruft `daysFromToday(order.eta)` zweimal, die Sortierung des
 * Handlungsbedarfs vergleicht drei Schluessel (in der Zahlungsansicht allein `paymentInitiatedAt`
 * absteigend).
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): nur `.ts`-Importe innerhalb von
 * `frontend/src`, kein `react`, kein `import.meta`, keine Browser-Globals.
 */
import { dokumentQuote } from "./dokumentStand.ts";
import { daysFromToday, normSearch, todayIso } from "./format.ts";
import { darfBestellungOeffnen } from "./bestellungSichtbar.ts";
import { istBestellungNachLaenderFilterSichtbar } from "./laender.ts";
import type { LaenderFilter } from "./laender.ts";
import { isPaid, isPaymentRequestTask, openPaymentRequestTask, nextDue } from "./payments.ts";
import {
  actionKind,
  activeReportingOrders,
  isArrivedStatus,
  overviewActionInfo,
  paymentRequestActionInfo,
} from "./status.ts";
import type { ActionInfo, Order, OrderPayment, OrderTask, StatusDefinitionen } from "./status.ts";
import { istBestellungNachTransportartFilterSichtbar } from "./transportart.ts";
import type { TransportartFilter } from "./transportart.ts";
import type { Supplier } from "./types.ts";

/**
 * Nur die beiden Felder, die `renderOverview` von `CURRENT_USER` liest (index.html:14596). Bewusst
 * KEIN Import von `state/reduzierer.ts#Nutzer`: die Berechnung bleibt damit ohne Store lauffaehig, und
 * der echte `Nutzer` ist zu dieser Form strukturell zuweisbar.
 */
export interface UebersichtNutzer {
  displayName?: string;
  username?: string;
}

/** Eine Zeile aus `actionRows` (index.html:14574-14583). */
export interface Aktionszeile {
  order: Order;
  info: ActionInfo;
  /** `nextDue(order) || order.eta || '9999-12-31'` - nur fuer die Sortierung. */
  due: string;
}

/** Eine Zeile aus `allTasks` (index.html:14597-14605). */
export interface Aufgabenzeile {
  order: Order;
  task: OrderTask;
  taskIndex: number;
  due: string;
  days: number;
  overdue: boolean;
  mine: boolean;
}

export interface UebersichtEingabe {
  bestellungen: readonly Order[];
  lieferanten: readonly Supplier[] | null | undefined;
  /** Rueckgabe von `setStatusDefinitions(boardSpalten)`. */
  definitionen: StatusDefinitionen;
  /** `isPaymentManager()` - der Aufrufer loest die Rolle auf. */
  istZahlungsansicht: boolean;
  nutzer: UebersichtNutzer | null;
  /** Der EU/China-Laenderfilter der Kopfzeile, GANZ VORN auf `bestellungen` angewandt. */
  laenderFilter: LaenderFilter;
  /** Der Transportart-Filter der Kopfzeile, an derselben Stelle angewandt. */
  transportartFilter: TransportartFilter;
}

/** Eine offene Rate mit ihrer Bestellung und den Tagen bis zur Faelligkeit. */
export interface OffeneRate {
  order: Order;
  payment: OrderPayment;
  days: number;
}

/** Das Ergebnis - dieselben Schluessel wie das `useMemo`-Objekt der Seite vor der Verschiebung. */
export interface UebersichtDaten {
  today: string;
  active: Order[];
  paymentManagerView: boolean;
  inTransit: Order[];
  unpaid: Order[];
  actionRows: Aktionszeile[];
  urgentActions: Aktionszeile[];
  actionCounts: { all: number; payment: number; eta: number; master: number };
  openTasks: Aufgabenzeile[];
  overdueTasks: Aufgabenzeile[];
  myTasks: Aufgabenzeile[];
  overduePayments: OffeneRate[];
  dueSoonPayments: OffeneRate[];
  arrivingSoon: Order[];
  missingEta: Order[];
  masterIssues: Aktionszeile[];
  documentTotals: { done: number; total: number };
  documentPct: number;
  topSupplier: [string, number] | undefined;
  statusCounts: Array<{ status: StatusDefinitionen["STATUSES"][number]; count: number }>;
  progressPct: number;
}

export function uebersichtAuswertung(e: UebersichtEingabe): UebersichtDaten {
  const { bestellungen, lieferanten, definitionen, istZahlungsansicht, nutzer, laenderFilter, transportartFilter } = e;
  // Laenderfilter (siehe Kopfkommentar am Prop in `UebersichtSeite.tsx`): GANZ VORN, bevor irgendeine der
  // folgenden Kennzahlen (Handlungsbedarf, Aufgaben, Zahlungsfristen, Historie, ...) aus der Liste
  // abgeleitet wird - dieselbe geteilte Regel wie in `BoardSeite.tsx`.
  const bestellungenSichtbar = bestellungen
    .filter(darfBestellungOeffnen)
    .filter((order) => istBestellungNachLaenderFilterSichtbar(order, lieferanten, laenderFilter))
    .filter((order) => istBestellungNachTransportartFilterSichtbar(order, transportartFilter));
  // index.html:14564-14572
  const today = todayIso();
  const active = activeReportingOrders(bestellungenSichtbar, definitionen);
  const paymentManagerView = istZahlungsansicht;
  // Der fruehere Split-Status-Ausschluss (isSplitStatusOrder) ist mit der Statusbereinigung
  // entfallen: der verbleibende Archiviert-Ausschluss (mit der Ausnahme fuer eine offene
  // Zahlungsaufgabe) deckt eine Sammellieferungs-Quelle bereits ab, die seither archived=true
  // bekommt (siehe Kopfkommentar von activeReportingOrders in lib/status.ts).
  //
  // BEFUND 3 (Nachbesserung "Serverseitiger Archivfilter + Ladeanzeige"): die Ausnahme
  // `!!openPaymentRequestTask(order)` findet nur, was ueberhaupt in `bestellungen` steht - seit der
  // Archivfilter den Store serverseitig verkleinert, waere sie fuer eine WIRKLICH archivierte
  // Bestellung mit offener Zahlungsaufgabe ins Leere gelaufen. Nicht hier repariert (diese Berechnung
  // ist reine Anzeige, kein Ladezweig): `App.tsx`/`Speicher.tsx` laden fuer `istZahlungsansicht`
  // (Rolle `payment_manager`) seither IMMER mit `archiv=all`, unabhaengig vom Board-Archivfilter -
  // `bestellungen` (das Prop) enthaelt archivierte Zeilen fuer diese Rolle deshalb wieder.
  const workflowOrders = paymentManagerView
    ? bestellungenSichtbar.filter((order) => !order.archived || !!openPaymentRequestTask(order))
    : active;
  const inTransit = active.filter((order) => !isArrivedStatus(order.status as string, definitionen));
  const unpaid = active.filter((order) => !isPaid(order));

  // index.html:14574-14594
  const actionRows: Aktionszeile[] = workflowOrders
    .map((order) => ({
      order,
      info: paymentManagerView ? paymentRequestActionInfo(order) : overviewActionInfo(order, definitionen),
      due: (nextDue(order) || order.eta || "9999-12-31") as string,
    }))
    .filter((row) => row.info.level > 0)
    .sort((a, b) =>
      paymentManagerView
        ? String(b.order.paymentInitiatedAt || "").localeCompare(String(a.order.paymentInitiatedAt || ""))
        : b.info.level - a.info.level ||
          a.due.localeCompare(b.due) ||
          String(b.order.orderDate || "").localeCompare(String(a.order.orderDate || "")),
    );
  const urgentActions = actionRows.filter((row) => row.info.level === 2);
  const actionCounts = {
    all: actionRows.length,
    payment: actionRows.filter((row) => actionKind(row.info.labels) === "payment").length,
    eta: actionRows.filter((row) => actionKind(row.info.labels) === "eta").length,
    master: actionRows.filter((row) => actionKind(row.info.labels) === "master").length,
  };

  // index.html:14596-14615
  const currentAssignee = normSearch(nutzer?.displayName || nutzer?.username || "");
  const allTasks: Aufgabenzeile[] = workflowOrders
    .flatMap((order) =>
      ((order.tasks || []) as OrderTask[]).map((task, taskIndex) => {
        const due = String(task.due || "");
        const days = due ? (daysFromToday(due) as number) : Infinity;
        return {
          order,
          task,
          taskIndex,
          due,
          days,
          overdue: !task.done && !!due && due < today,
          mine: !!currentAssignee && normSearch(task.assignee) === currentAssignee,
        };
      }),
    )
    .filter((row) => !paymentManagerView || isPaymentRequestTask(row.task));
  const openTasks = allTasks
    .filter((row) => !row.task.done)
    .sort(
      (a, b) =>
        Number(b.overdue) - Number(a.overdue) ||
        (a.due || "9999-12-31").localeCompare(b.due || "9999-12-31"),
    );
  const overdueTasks = openTasks.filter((row) => row.overdue);
  const myTasks = openTasks.filter((row) => row.mine);

  // index.html:14617-14624
  const openPayments = active.flatMap((order) =>
    ((order.payments || []) as OrderPayment[])
      .filter((payment) => !payment.paid && payment.due)
      .map((payment) => ({ order, payment, days: daysFromToday(payment.due as string) as number })),
  );
  const overduePayments = openPayments.filter((row) => (row.payment.due as string) < today);
  const dueSoonPayments = openPayments.filter(
    (row) => (row.payment.due as string) >= today && row.days <= 7,
  );
  const arrivingSoon = active.filter(
    (order) =>
      order.eta &&
      (daysFromToday(order.eta as string) as number) >= 0 &&
      (daysFromToday(order.eta as string) as number) <= 14,
  );
  const missingEta = inTransit.filter((order) => !order.eta);
  const masterIssues = actionRows.filter((row) => actionKind(row.info.labels) === "master");

  // index.html:14626-14632
  // ABWEICHUNG vom Original (Spec 2026-10-31-board-dokumentstand): das Original summiert
  // `docProgress(order)` (alle sichtbaren Dokumentarten aus `pdfs[]`). Hier zaehlt das Lesefeld
  // `dokumentStand` des Servers (`lib/dokumentStand.ts#dokumentQuote`) - nur Pflichtbelege,
  // „entfaellt" gilt als erledigt, dieselbe Bewertung wie Board und Checkliste. Eine Bestellung ohne
  // gueltiges Feld (JTL-Bestellung ohne Hub-Zeile) liefert 0/0 und zaehlt weder im Zaehler noch im Nenner.
  // Die Typangabe an `reduce` ist Pflicht, keine Kosmetik: `Order` hat eine
  // Index-Signatur (`lib/types.ts`), weshalb `{ done: 0, total: 0 }` fuer TypeScript auch als
  // `Order` durchgeht und die Ueberladung `reduce(cb, initial: Order)` gewinnen wuerde - `sum.done`
  // waere dann `unknown`. Reine Typarbeit ohne Laufzeitwirkung.
  const documentTotals = active.reduce<{ done: number; total: number }>(
    (sum, order) => {
      const progress = dokumentQuote(order);
      sum.done += progress.done;
      sum.total += progress.total;
      return sum;
    },
    { done: 0, total: 0 },
  );
  const documentPct = documentTotals.total
    ? Math.round((documentTotals.done / documentTotals.total) * 100)
    : 100;

  // index.html:14634-14639
  const supplierCounts = new Map<string, number>();
  for (const order of active) {
    const supplier = (order.supplier || "Ohne Lieferant") as string;
    supplierCounts.set(supplier, (supplierCounts.get(supplier) || 0) + 1);
  }
  const topSupplier = [...supplierCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "de"),
  )[0];

  // index.html:14641-14649
  const statusCounts = definitionen.STATUSES.map((status) => ({
    status,
    count: active.filter((order) => order.status === status.id).length,
  }));
  const progressPct = active.length
    ? Math.round(
        (active.reduce((sum, order) => sum + (Number(definitionen.STATUS_STAGE[order.status as string]) || 0), 0) /
          active.length /
          7) *
          100,
      )
    : 0;
  return {
    today,
    active,
    paymentManagerView,
    inTransit,
    unpaid,
    actionRows,
    urgentActions,
    actionCounts,
    openTasks,
    overdueTasks,
    myTasks,
    overduePayments,
    dueSoonPayments,
    arrivingSoon,
    missingEta,
    masterIssues,
    documentTotals,
    documentPct,
    topSupplier,
    statusCounts,
    progressPct,
  };
}
